// =============================================================================
// api/scenes.ts
// =============================================================================

import type { Media, ReorderItem, SceneRow, VScene } from "@/types";
import { supabase } from "@cl/supabase";
import { handleSupabaseError } from "./_error";
import { deleteMedia } from "./media";

// <-- NUEVO: Agregado "audio_id" al Pick
type UpdateScenePayload = Partial<
  Pick<SceneRow, "name" | "img_id" | "audio_id" | "img_b_id" | "variant_kind" | "sort_order" | "is_main" | "force_initial_view" | "room_id" | "floor_id" | "sphere_roll" | "sphere_pitch" | "sphere_yaw" | "north_offset" | "measures" | "calibration" | "mask_zones" | "map_pos">
>;

// GET
export const getInitialScene = <T extends Partial<VScene>>(
  scenes: T[],
): T | null => {
  if (!scenes.length) return null;

  return scenes.reduce((best, current) => {
    const getScore = (s: T) => {
      let score = 0;
      if (s.floor_is_main) score += 1000;
      if (s.room_is_main) score += 100;
      if (s.scene_is_main) score += 10;
      score -= (Number(s.floor_order) || 0) * 1;
      return score;
    };

    return getScore(current) > getScore(best) ? current : best;
  });
};

export const fetchSceneView = async (sceneId: string): Promise<VScene | null> => {
  const { data, error } = await supabase
    .from("v_scenes")
    .select("*")
    .eq("scene_id", sceneId)
    .single();
  if (error) return null;
  return data as VScene;
};

export const fetchTourScenesView = async (
  tourId: string,
): Promise<VScene[]> => {
  const { data, error } = await supabase
    .from("v_scenes")
    .select("*")
    .eq("tour_id", tourId)
    .order("floor_order")
    .order("room_order")
    .order("scene_order")
    .limit(500);

  if (error) handleSupabaseError(error, "fetchTourScenesView");
  return (data ?? []) as VScene[];
};

export const fetchScenesByRoom = async (
  roomId: string,
): Promise<SceneRow[]> => {
  const { data, error } = await supabase
    .from("scenes")
    .select("*")
    .eq("room_id", roomId)
    .order("sort_order", { ascending: true });

  if (error) handleSupabaseError(error, "fetchScenesByRoom");
  return (data ?? []) as SceneRow[];
};

export const createScene = async ({
  room_id,
  tour_id,
  floor_id,
  name,
  img_id,
  sort_order = 0,
}: {
  room_id?: string | null;
  tour_id: string;
  floor_id?: string | null;
  name: string;
  img_id: string;
  sort_order?: number;
}): Promise<SceneRow> => {
  if (!tour_id)
    throw new Error("tour_id is strictly required for RLS policies");

  const { data, error } = await supabase
    .from("scenes")
    .insert([
      {
        room_id: room_id ?? null,
        tour_id: tour_id,
        floor_id: floor_id ?? null,
        name,
        img_id,
        sort_order,
      },
    ])
    .select("*")
    .single();

  if (error) handleSupabaseError(error, "createScene");

  const anchorKey = room_id ? "room_id" : "floor_id";
  const anchorVal = room_id ?? floor_id;
  const { count } = await supabase
    .from("scenes")
    .select("*", { count: "exact", head: true })
    .eq(anchorKey, anchorVal!);

  if (count === 1) {
    await supabase.from("scenes").update({ is_main: true }).eq("id", data!.id);
  }

  return data as SceneRow;
};

export const updateScene = async (
  id: string,
  updates: UpdateScenePayload,
): Promise<void> => {
  const { error } = await supabase.from("scenes").update(updates).eq("id", id);
  if (error) handleSupabaseError(error, "updateScene");
};

/** Per-scene floor-plan position (opt-in). null = unplace (falls back to room).
 *  Mirrors rooms updateRoomMapPos. */
export const updateSceneMapPos = (
  id: string,
  map_pos: { x: number; y: number } | null,
) => updateScene(id, { map_pos });

export const deleteScene = async (scene: VScene): Promise<void> => {
  // Hotspots owned by this scene (media cleanup — rows deleted by DB CASCADE)
  const { data: hotspots } = await supabase
    .from("hotspots")
    .select("img_id, audio_id, video_id")
    .eq("scene_id", scene.scene_id);

  // Nav hotspots in OTHER scenes pointing here (DB sets target_scene_id = null, rows survive)
  const { data: inboundNavHotspots } = await supabase
    .from("hotspots")
    .select("id, img_id, audio_id, video_id")
    .eq("target_scene_id", scene.scene_id);

  const { error } = await supabase
    .from("scenes")
    .delete()
    .eq("id", scene.scene_id);
  if (error) handleSupabaseError(error, "deleteScene");

  // Delete inbound nav hotspots (now orphaned with target_scene_id = null)
  if (inboundNavHotspots?.length) {
    const ids = inboundNavHotspots.map((h) => h.id);
    await supabase.from("hotspots").delete().in("id", ids);
  }

  // Eliminar imagen principal
  if (scene.scene_img_id) {
    const { data: m } = await supabase
      .from("media")
      .select("*")
      .eq("id", scene.scene_img_id)
      .single();
    if (m) await deleteMedia(m as Media);
  }

  // Eliminar thumbnail
  if (scene.scene_thumb_id) {
    const { data: m } = await supabase
      .from("media")
      .select("*")
      .eq("id", scene.scene_thumb_id)
      .single();
    if (m) await deleteMedia(m as Media);
  }

  // <-- NUEVO: Eliminar archivo de audio ambiente de la escena
  if (scene.scene_audio_id) {
    const { data: m } = await supabase
      .from("media")
      .select("*")
      .eq("id", scene.scene_audio_id)
      .single();
    if (m) await deleteMedia(m as Media);
  }

  // Delete media from all affected hotspots (owned + inbound nav)
  const allHotspotMediaIds = [
    ...(hotspots ?? []),
    ...(inboundNavHotspots ?? []),
  ]
    .flatMap((h) => [h.img_id, h.audio_id, h.video_id].filter(Boolean)) as string[];

  if (allHotspotMediaIds.length) {
    const { data: records } = await supabase
      .from("media")
      .select("*")
      .in("id", allHotspotMediaIds);
    if (records)
      await Promise.allSettled(records.map((m) => deleteMedia(m as Media)));
  }
};

export const setMainScene = async (sceneId: string): Promise<void> => {
  const { error } = await supabase.rpc("set_main_scene", {
    target_scene_id: sceneId,
  });
  if (error) handleSupabaseError(error, "setMainScene");
};

export const reorderScenes = async (scenes: ReorderItem[]): Promise<void> => {
  const { error } = await supabase.rpc("update_scene_orders", {
    payload: scenes,
  });
  if (error) handleSupabaseError(error, "reorderScenes");
};
